import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { alignableObjectsInSelection } from '@peruselab/structure-editor/domain/objectAlignment';
import { ALIGN, DISTRIBUTE, ORDER } from '../../components/panels/arrangeActions';
import { Icon } from '../../components/Icon';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import type { ToolbarGroup } from './useToolbarOverflow';

const MOBILE = '(max-width: 960px)';
function subscribe(listener: () => void) {
  const query = window.matchMedia(MOBILE);
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}
const mobileSnapshot = () => window.matchMedia(MOBILE).matches;

/** Optional whole groups: desktop shortcuts stop when space runs out; Arrange stays complete. */
export function SelectionArrangeControls({
  doc,
  hiddenGroups,
  menu = false,
  onAction
}: {
  readonly doc: UseDrawingDocumentResult;
  readonly hiddenGroups: ReadonlySet<ToolbarGroup>;
  readonly menu?: boolean;
  readonly onAction?: () => void;
}) {
  const { t } = useTranslation();
  const mobile = useSyncExternalStore(subscribe, mobileSnapshot, () => false);
  if (menu && !mobile) return null;
  const objects = alignableObjectsInSelection(doc.document, doc.selection, doc.reactionParts);
  const annotations = doc.selection.annotationIds?.length ?? 0;
  const hasMolecule = doc.selection.atomIds.length > 0 || doc.selection.bondIds.length > 0;
  const groups = [
    {
      key: 'align' as const,
      label: t('rightRail.align'),
      eligible: objects.length >= 2,
      actions: ALIGN.map(([kind, icon, label]) => ({
        key: kind,
        icon,
        label: t(`editorToolPalette.align.${label}`),
        run: () => doc.alignSelection(kind, { relativeTo: 'objects' })
      }))
    },
    {
      key: 'distribute' as const,
      label: t('rightRail.arrange.distribute'),
      eligible: objects.length >= 3,
      actions: DISTRIBUTE.map(([kind, icon, label]) => ({
        key: kind,
        icon,
        label: t(`editorToolPalette.align.${label}`),
        run: () => doc.alignSelection(kind)
      }))
    },
    {
      key: 'order' as const,
      label: t('rightRail.arrange.order'),
      eligible: annotations > 0 && !hasMolecule,
      actions: ORDER.map(([command, icon]) => ({
        key: command,
        icon,
        label: t(`rightRail.arrange.${command}`),
        run: () => doc.reorderSelection(command)
      }))
    }
  ];
  return (
    <>
      {groups
        .filter((group) => group.eligible && (!menu || hiddenGroups.has(group.key)))
        .map((group) => (
          <div
            key={group.key}
            role="group"
            aria-label={group.label}
            className={menu ? 'workspace-more-group' : 'workspace-action-group'}
            data-toolbar-group={menu ? undefined : group.key}
            hidden={!menu && hiddenGroups.has(group.key)}
          >
            {group.actions.map((action) => (
              <button
                key={action.key}
                type="button"
                role={menu ? 'menuitem' : undefined}
                className={menu ? 'workspace-more-item workspace-more-action' : 'icon-button'}
                aria-label={action.label}
                title={action.label}
                onClick={() => {
                  action.run();
                  onAction?.();
                }}
              >
                <Icon name={action.icon} />
                {menu ? <span>{action.label}</span> : null}
              </button>
            ))}
          </div>
        ))}
    </>
  );
}
